import { ref } from 'vue'

export const STORAGE_KEY = 'bottle_revelio'

// One key replaced three. Anyone who had already revealed a surface keeps it, and
// the old keys are cleared on the way through so a later unrevelio cannot be
// undone by a stale one on the next load.
const RETIRED_KEYS = ['bottleBotWidgetsRevealed', 'bottle_lead_form_revealed', 'bottle_embed_test_page_revealed']

const REVEAL_WORD = 'revelio'
const CONCEAL_WORD = 'unrevelio'
const BUFFER_MAX = CONCEAL_WORD.length

const truthy = value => value === '1' || value === 'true'

const read = key => {
  try {
    return localStorage.getItem(key)
  } catch {
    // Private windows and blocked site data throw on access. The reveal stops
    // persisting rather than taking the page down with it.
    return null
  }
}

const write = value => {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, '1')
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // As above - storing is best effort.
  }
}

const dropRetiredKeys = () => {
  try {
    RETIRED_KEYS.forEach(key => localStorage.removeItem(key))
  } catch {
    // As above.
  }
}

// Module scope, not component state: the word is typed anywhere in the app, and
// what it reveals is usually nowhere near the surface that heard it.
const revealed = ref(false)
let hydrated = false

// Read on first use rather than at import: this module is imported before a test
// can stub localStorage, and a read at import time would freeze on whatever it
// found there.
const hydrate = () => {
  if (hydrated) return
  hydrated = true
  const stored = truthy(read(STORAGE_KEY))
  const inherited = !stored && RETIRED_KEYS.some(key => truthy(read(key)))
  revealed.value = stored || inherited
  if (inherited) write(true)
  dropRetiredKeys()
}

const set = value => {
  hydrate()
  if (revealed.value === value) return
  revealed.value = value
  write(value)
}

export function useRevealed() {
  hydrate()
  return revealed
}

export function isRevealed() {
  hydrate()
  return revealed.value
}

export function reveal() {
  set(true)
}

export function conceal() {
  set(false)
}

// Mounted once, by App, so the word works on any page rather than only on the one
// that happens to render what it reveals.
export function createRevelioKeydownHandler() {
  let buffer = ''

  return event => {
    const key = event?.key
    if (!key || key.length !== 1 || !/[a-z]/i.test(key)) return
    if (event.metaKey || event.ctrlKey || event.altKey) return

    buffer = (buffer + key.toLowerCase()).slice(-BUFFER_MAX)

    // "unrevelio" ends in "revelio", so the conceal word has to be tested first
    // or typing it would reveal instead of hide.
    if (buffer.endsWith(CONCEAL_WORD)) {
      buffer = ''
      conceal()
    } else if (buffer.endsWith(REVEAL_WORD)) {
      buffer = ''
      reveal()
    }
  }
}
